<template>
  <div class="flex flex-col h-full w-full bg-gray-100 dark:bg-gray-950">
    <div
      v-if="sourceView"
      class="min-h-0 flex-1 flex flex-col overflow-hidden"
    >
      <div class="flex items-center gap-1 p-2 border-b bg-white dark:bg-gray-900">
        <UButton
          icon="i-lucide-code-2"
          size="xs"
          variant="soft"
          color="primary"
          :aria-label="$t('components.elements.email.source.close')"
          :title="$t('components.elements.email.source.close')"
          @click="closeSourceView"
        />
      </div>
      <textarea
        v-model="modelValueLocal"
        class="min-h-0 flex-1 w-full resize-none bg-gray-950 p-4 font-mono text-sm text-gray-100 focus:outline-none"
        :aria-label="$t('components.elements.email.source.label')"
        spellcheck="false"
      />
    </div>

    <UEditor
      v-else
      ref="uEditor"
      v-model="modelValueLocal"
      content-type="html"
      :image="false"
      :placeholder="placeholder ?? undefined"
      :mention="mentionConfig"
      :extensions="extraExtensions"
      class="min-h-0 flex-1 overflow-hidden"
      :ui="{
        root: 'min-h-0 flex-1 flex flex-col overflow-hidden',
        content: 'min-h-0 flex-1 overflow-auto p-3 sm:p-6 prose max-w-none focus:outline-none',
        base: 'box-border min-h-full w-full max-w-[680px] mx-auto bg-white px-5 py-6 sm:px-6 text-gray-900 shadow-sm ring-1 ring-gray-200 dark:ring-gray-700 focus:outline-none'
      }"
    >
      <template #default="{ editor }">
        <div
          v-if="editor"
          class="shrink-0 border-b bg-white dark:border-gray-800 dark:bg-gray-900"
        >
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2 p-2">
            <div class="flex items-center gap-1">
              <UButton
                v-for="btn in primaryMarks"
                :key="btn.mark"
                :icon="btn.icon"
                size="xs"
                :variant="editor.isActive(btn.mark) ? 'soft' : 'ghost'"
                :color="editor.isActive(btn.mark) ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive(btn.mark)"
                :aria-label="btn.label"
                :title="btn.label"
                @click="btn.run(editor)"
              />
              <UButton
                icon="i-lucide-strikethrough"
                size="xs"
                :variant="editor.isActive('strike') ? 'soft' : 'ghost'"
                :color="editor.isActive('strike') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('strike')"
                :aria-label="$t('components.elements.email.format.strikethrough')"
                :title="$t('components.elements.email.format.strikethrough')"
                @click="toggleStrike(editor)"
              />
            </div>

            <div class="flex items-center gap-1">
              <UButton
                icon="i-lucide-link"
                size="xs"
                :variant="editor.isActive('link') || editor.isActive('emailButton') ? 'soft' : 'ghost'"
                :color="editor.isActive('link') || editor.isActive('emailButton') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('link') || editor.isActive('emailButton')"
                :aria-label="$t('components.elements.email.link.title')"
                :title="$t('components.elements.email.link.title')"
                @click="openLink(editor)"
              />

              <label
                class="relative h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                :title="$t('components.elements.email.format.textColour')"
              >
                <UIcon name="i-lucide-palette" class="size-4" />
                <span class="absolute bottom-0.5 left-1 right-1 h-0.5 rounded" :style="{ backgroundColor: textColour }" />
                <input
                  v-model="textColour"
                  type="color"
                  class="absolute inset-0 opacity-0 cursor-pointer"
                  :aria-label="$t('components.elements.email.format.textColour')"
                  @input="setTextColour(editor)"
                >
              </label>
            </div>

            <div class="flex items-center gap-1">
              <UButton
                v-for="heading in headings"
                :key="`heading-${heading.level ?? 'paragraph'}`"
                :icon="heading.icon"
                size="xs"
                :variant="heading.isActive(editor) ? 'soft' : 'ghost'"
                :color="heading.isActive(editor) ? 'primary' : 'neutral'"
                :aria-pressed="heading.isActive(editor)"
                :aria-label="heading.label"
                :title="heading.label"
                @click="heading.run(editor)"
              />
            </div>

            <div class="flex items-center gap-1">
              <UButton
                icon="i-lucide-list"
                size="xs"
                :variant="editor.isActive('bulletList') ? 'soft' : 'ghost'"
                :color="editor.isActive('bulletList') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('bulletList')"
                :title="$t('components.elements.email.format.bulletList')"
                @click="toggleBulletList(editor)"
              />
              <UButton
                icon="i-lucide-list-ordered"
                size="xs"
                :variant="editor.isActive('orderedList') ? 'soft' : 'ghost'"
                :color="editor.isActive('orderedList') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('orderedList')"
                :title="$t('components.elements.email.format.orderedList')"
                @click="toggleOrderedList(editor)"
              />
              <UButton
                icon="i-lucide-quote"
                size="xs"
                :variant="editor.isActive('blockquote') ? 'soft' : 'ghost'"
                :color="editor.isActive('blockquote') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('blockquote')"
                :title="$t('components.elements.email.format.quote')"
                @click="toggleQuote(editor)"
              />
            </div>

            <div class="flex items-center gap-1">
              <UButton
                v-for="item in aligns"
                :key="item.align"
                :icon="item.icon"
                size="xs"
                :variant="editor.isActive({ textAlign: item.align }) ? 'soft' : 'ghost'"
                :color="editor.isActive({ textAlign: item.align }) ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive({ textAlign: item.align })"
                :aria-label="item.label"
                :title="item.label"
                @click="setAlignment(editor, item.align)"
              />
            </div>

            <div class="flex items-center gap-1">
              <UButton
                icon="i-lucide-minus"
                size="xs"
                variant="ghost"
                :aria-label="$t('components.elements.email.format.divider')"
                :title="$t('components.elements.email.format.divider')"
                @click="insertDivider(editor)"
              />
              <UButton
                icon="i-lucide-table-2"
                size="xs"
                :variant="editor.isActive('table') ? 'soft' : 'ghost'"
                :color="editor.isActive('table') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('table')"
                :aria-label="$t('components.elements.email.format.table')"
                :title="$t('components.elements.email.format.table')"
                @click="insertTable(editor)"
              />
              <UButton
                icon="i-lucide-image"
                size="xs"
                :variant="editor.isActive('image') ? 'soft' : 'ghost'"
                :color="editor.isActive('image') ? 'primary' : 'neutral'"
                :aria-pressed="editor.isActive('image')"
                :aria-label="$t('components.elements.email.image.title')"
                :title="$t('components.elements.email.image.title')"
                @click="openImage(editor)"
              />
              <UButton
                v-if="placeholderItems.length"
                icon="i-lucide-braces"
                size="xs"
                :variant="showPlaceholderTray ? 'soft' : 'ghost'"
                :color="showPlaceholderTray ? 'primary' : 'neutral'"
                :aria-pressed="showPlaceholderTray"
                :aria-label="$t('components.elements.email.placeholders')"
                :title="$t('components.elements.email.placeholders')"
                @click="togglePlaceholderTray"
              />
            </div>

            <div class="ml-auto flex shrink-0 items-center gap-1">
              <UButton
                v-if="source"
                icon="i-lucide-code-2"
                size="xs"
                variant="ghost"
                :aria-label="$t('components.elements.email.source.open')"
                :title="$t('components.elements.email.source.open')"
                @click="openSourceView"
              />

              <UButton
                icon="i-lucide-undo-2"
                size="xs"
                variant="ghost"
                :disabled="!editor.can().undo()"
                @click="undo(editor)"
              />
              <UButton
                icon="i-lucide-redo-2"
                size="xs"
                variant="ghost"
                :disabled="!editor.can().redo()"
                @click="redo(editor)"
              />
            </div>
          </div>

          <div
            v-if="showPlaceholderTray"
            class="flex items-start gap-3 border-t border-gray-200 px-3 py-2 dark:border-gray-800"
          >
            <span class="shrink-0 pt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {{ $t('components.elements.email.placeholders') }}
            </span>
            <div class="flex flex-1 flex-wrap gap-1.5">
              <UButton
                v-for="placeholderItem in placeholderItems"
                :key="placeholderItem.id"
                size="xs"
                color="neutral"
                variant="soft"
                @click="insertPlaceholder(editor, placeholderItem.value)"
              >
                {{ placeholderItem.label }}
              </UButton>
            </div>
            <UButton
              icon="i-lucide-x"
              size="xs"
              color="neutral"
              variant="ghost"
              :aria-label="$t('general.close')"
              @click="closePlaceholderTray"
            />
          </div>

          <div
            v-if="editor.isActive('table')"
            class="flex flex-wrap items-center gap-1.5 border-t border-gray-200 px-3 py-2 dark:border-gray-800"
          >
            <span class="mr-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {{ $t('components.elements.email.format.table') }}
            </span>
            <UButton size="xs" color="neutral" variant="soft" @click="addTableRow(editor)">
              {{ $t('components.elements.email.format.addRow') }}
            </UButton>
            <UButton size="xs" color="neutral" variant="soft" @click="addTableColumn(editor)">
              {{ $t('components.elements.email.format.addColumn') }}
            </UButton>
            <UButton size="xs" color="neutral" variant="ghost" @click="deleteTableRow(editor)">
              {{ $t('components.elements.email.format.deleteRow') }}
            </UButton>
            <UButton size="xs" color="neutral" variant="ghost" @click="deleteTableColumn(editor)">
              {{ $t('components.elements.email.format.deleteColumn') }}
            </UButton>
            <UButton size="xs" color="error" variant="ghost" @click="deleteTable(editor)">
              {{ $t('components.elements.email.format.deleteTable') }}
            </UButton>
          </div>
        </div>
      </template>
    </UEditor>

    <EmailLinkDialog ref="linkDialog" :links="links" />
    <EmailImageDialog ref="imageDialog" :assets="assets" :upload="imageUpload" />
  </div>
</template>

<script setup lang="ts">
import TextAlign from '@tiptap/extension-text-align'
import { TableKit } from '@tiptap/extension-table'

import createSuggestion from './editor/suggestion'
import { EmailButton } from './email/EmailButton'
import { EmailImage } from './email/EmailImage'
import { EmailTextStyle } from './email/EmailTextStyle'
import EmailImageDialog from './email/ImageDialog.vue'
import EmailLinkDialog from './email/LinkDialog.vue'
import { normaliseEmailEditorAssets } from '@/lib/emailEditor'

interface Props {
  modelValue?: string
  placeholder?: string | null
  variables?: Record<string, any> | string[]
  links?: any[]
  name?: string | null
  image?: Record<string, any>
  source?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: null,
  variables: () => ({}),
  links: () => [],
  name: null,
  image: () => ({}),
  source: true,
})

const { $i18n } = useNuxtApp()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const uEditor = ref<any>(null)
const sourceView = ref(false)
const textColour = ref('#111827')
const showPlaceholderTray = ref(false)
const linkDialog = ref<any>(null)
const imageDialog = ref<any>(null)

const modelValueLocal = computed({
  get: () => props.modelValue,
  set: (val: string | null | undefined) => emit('update:modelValue', val ?? ''),
})

const placeholderItems = computed(() => {
  const list = Array.isArray(props.variables)
    ? props.variables
    : Object.values(props.variables ?? {})
  return list
    .filter((v): v is string => typeof v === 'string')
    .map((v) => ({
      id: v,
      label: v.replace(/{{\s*|\s*}}/g, ''),
      value: v,
    }))
})

const assets = computed(() => normaliseEmailEditorAssets(props.image?.select))
const imageUpload = computed(() => typeof props.image?.upload === 'function' ? props.image.upload : null)

// `{`-triggered typeahead via the project's existing Tippy-based suggestion UI.
// Picking an item replaces the trigger range with the raw placeholder string
// (e.g. "{{customer.displayName}}") so the saved HTML stays plain text.
const mentionConfig = computed(() => ({
  suggestion: {
    ...createSuggestion({
      getItems: ({ query }: { query: string }) => {
        const q = (query || '').toLowerCase()
        return placeholderItems.value.filter(item =>
          item.label.toLowerCase().includes(q) ||
          item.value.toLowerCase().includes(q),
        )
      },
    }),
    command: ({ editor, range, props: itemProps }: any) => {
      editor.chain().focus().deleteRange(range).insertContent(itemProps.value).run()
    },
  },
}))

const extraExtensions = [
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  EmailTextStyle,
  TableKit.configure({
    table: {
      HTMLAttributes: {
        style: 'border-collapse:collapse;width:100%;margin:16px 0',
      },
    },
    tableCell: {
      HTMLAttributes: {
        style: 'border:1px solid #d1d5db;padding:8px;vertical-align:top',
      },
    },
    tableHeader: {
      HTMLAttributes: {
        style: 'border:1px solid #d1d5db;padding:8px;vertical-align:top;background-color:#f3f4f6;font-weight:600',
      },
    },
  }),
  EmailButton,
  EmailImage,
]

const primaryMarks = computed(() => [
  { mark: 'bold', icon: 'i-lucide-bold', label: $i18n.t('components.elements.email.format.bold'), run: (e: any) => { e.chain().focus().toggleBold().run() } },
  { mark: 'italic', icon: 'i-lucide-italic', label: $i18n.t('components.elements.email.format.italic'), run: (e: any) => { e.chain().focus().toggleItalic().run() } },
  { mark: 'underline', icon: 'i-lucide-underline', label: $i18n.t('components.elements.email.format.underline'), run: (e: any) => { e.chain().focus().toggleUnderline().run() } },
])

const headings = computed(() => [
  { level: null, icon: 'i-lucide-pilcrow', label: $i18n.t('components.elements.email.format.paragraph'), isActive: (e: any) => e.isActive('paragraph'), run: (e: any) => { e.chain().focus().setParagraph().run() } },
  { level: 1, icon: 'i-lucide-heading-1', label: $i18n.t('components.elements.email.format.heading', { level: 1 }), isActive: (e: any) => e.isActive('heading', { level: 1 }), run: (e: any) => { e.chain().focus().toggleHeading({ level: 1 }).run() } },
  { level: 2, icon: 'i-lucide-heading-2', label: $i18n.t('components.elements.email.format.heading', { level: 2 }), isActive: (e: any) => e.isActive('heading', { level: 2 }), run: (e: any) => { e.chain().focus().toggleHeading({ level: 2 }).run() } },
  { level: 3, icon: 'i-lucide-heading-3', label: $i18n.t('components.elements.email.format.heading', { level: 3 }), isActive: (e: any) => e.isActive('heading', { level: 3 }), run: (e: any) => { e.chain().focus().toggleHeading({ level: 3 }).run() } },
])

const aligns = computed(() => [
  { align: 'left', icon: 'i-lucide-align-left', label: $i18n.t('components.elements.email.format.alignLeft') },
  { align: 'center', icon: 'i-lucide-align-center', label: $i18n.t('components.elements.email.format.alignCenter') },
  { align: 'right', icon: 'i-lucide-align-right', label: $i18n.t('components.elements.email.format.alignRight') },
  { align: 'justify', icon: 'i-lucide-align-justify', label: $i18n.t('components.elements.email.format.alignJustify') },
])

function getEditor() {
  const editor = uEditor.value?.editor
  return editor?.value ?? editor ?? null
}

function insertPlaceholder(editor: any, value: string) {
  if (!editor) return
  editor.chain().focus().insertContent(value).run()
}

function togglePlaceholderTray () {
  showPlaceholderTray.value = !showPlaceholderTray.value
}

function closePlaceholderTray () {
  showPlaceholderTray.value = false
}

function openLink (editor: any) {
  linkDialog.value?.open(editor)
}

function openImage (editor: any) {
  imageDialog.value?.open(editor)
}

function openSourceView () {
  sourceView.value = true
}

function closeSourceView () {
  sourceView.value = false
}

function insertDivider (editor: any) {
  editor.chain().focus().setHorizontalRule().run()
}

function toggleStrike (editor: any) {
  editor.chain().focus().toggleStrike().run()
}

function toggleBulletList (editor: any) {
  editor.chain().focus().toggleBulletList().run()
}

function toggleOrderedList (editor: any) {
  editor.chain().focus().toggleOrderedList().run()
}

function toggleQuote (editor: any) {
  editor.chain().focus().toggleBlockquote().run()
}

function setAlignment (editor: any, alignment: string) {
  editor.chain().focus().setTextAlign(alignment).run()
}

function undo (editor: any) {
  editor.chain().focus().undo().run()
}

function redo (editor: any) {
  editor.chain().focus().redo().run()
}

function setTextColour (editor: any) {
  setEmailTextStyle(editor, { color: textColour.value })
}

function setEmailTextStyle (editor: any, attributes: Record<string, string>) {
  const current = editor.getAttributes('emailTextStyle')
  editor.chain().focus().setMark('emailTextStyle', { ...current, ...attributes }).run()
}

function insertTable (editor: any) {
  if (editor.isActive('table')) return
  editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()
}

function addTableRow (editor: any) {
  editor.chain().focus().addRowAfter().run()
}

function addTableColumn (editor: any) {
  editor.chain().focus().addColumnAfter().run()
}

function deleteTableRow (editor: any) {
  editor.chain().focus().deleteRow().run()
}

function deleteTableColumn (editor: any) {
  editor.chain().focus().deleteColumn().run()
}

function deleteTable (editor: any) {
  editor.chain().focus().deleteTable().run()
}

function insertImageAtCursor(url: string) {
  const editor = getEditor()
  if (!editor || !url) return
  editor.chain().focus().setImage({ src: url }).run()
}

function getImageUrl(image: any): string | null {
  if (!image) return null
  if (typeof image === 'string') return image
  if (image.file?.url) return image.file.url
  if (image.url) return image.url

  const first = Object.values(image)[0] as any
  return first?.file?.url ?? first?.url ?? null
}

function insertImage(image: any) {
  const url = getImageUrl(image)
  if (url) insertImageAtCursor(url)
}

defineExpose({ insertImage })
</script>
